import { apiClient } from './apiClient';

export class PatientService {
    constructor() {
        this.BASE_URL = '/patients';
    }

    /**
     * Create a new patient
     * @param {object} patientData
     */
    async createPatient(patientData) {
        const response = await apiClient.post(`${this.BASE_URL}/save`, patientData);
        return response
    }

    /**
     * Get a single patient by ID
     * @param {string} id
     */
    async getPatientById(id) {
        const response = await apiClient.get(`${this.BASE_URL}/${id}`);
        return response
    }

    /**
     * Get all patients with optional filters and pagination
     * Backend route: GET /patients/:orgId?page=&limit=&firstName=&lastName=&contactPhone=&dateOfBirth=
     * @param {string} orgId
     * @param {object} query - { page, limit, firstName, lastName, contactPhone, dateOfBirth }
     */
    async getAllPatients(query = {}) {
        const params = new URLSearchParams(query).toString();
        const url = `${this.BASE_URL}/${params ? `?${params}` : ''}`;
        const response = await apiClient.get(url);
        return response // returns { data, pagination }
    }

    /**
     * Update a patient
     * @param {string} patientId
     * @param {object} updateData
     */
    async updatePatient(patientId, updateData) {
        const response = await apiClient.patch(`${this.BASE_URL}/${patientId}`, updateData);
        return response
    }

    /**
     * Search patients by name (scalable server-side search)
     * @param {string} firstName - First name search term (optional)
     * @param {string} lastName - Last name search term (optional)
     * @param {number} limit - Max results (default 50)
     * @returns {Promise<Array>} Array of patient search results
     */
    async searchPatients(firstName = '', lastName = '', limit = 50) {
        const params = new URLSearchParams();
        if (firstName) params.set('firstName', firstName);
        if (lastName) params.set('lastName', lastName);
        params.set('limit', limit.toString());

        const response = await apiClient.get(`${this.BASE_URL}/search?${params.toString()}`);
        return response;
    }

    /**
     * Get patient by Rx Number
     * @param {string} rxNumber 
     */
    async getPatientByRx(rxNumber) {
        if (!rxNumber) return null;
        try {
            const response = await apiClient.get(`${this.BASE_URL}/by-rx/${encodeURIComponent(rxNumber)}`);
            return response;
        } catch (error) {
            console.error('Error fetching patient by Rx:', error);
            return null;
        }
    }

    /**
     * Add an entity association to a patient
     * @param {string} patientId
     * @param {object} association - The association data (e.g., { entityType: 'pharmacy', entityId: 'pharmacy123' })
     */
    async addEntityAssociation(patientId, association) {
        try {
            const response = await apiClient.post(`${this.BASE_URL}/${patientId}/associations`, association);
            return response;
        } catch (error) {
            console.error("Error adding entity association:", error);
            throw error;
        }
    }

    /**
     * Mark an entity association as current for a patient
     * @param {string} patientId
     * @param {string} entityType - The type of entity (e.g., 'pharmacy', 'provider')
     * @param {string} entityId - The ID of the entity to mark as current
     */
    async markEntityAsCurrent(patientId, entityType, entityId) {
        try {
            const response = await apiClient.patch(`${this.BASE_URL}/${patientId}/associations/${entityType}/${entityId}/current`);
            return response;
        } catch (error) {
            console.error("Error marking entity as current:", error);
            throw error;
        }
    }

    /**
     * Update details of a specific association (e.g. claim ref id)
     * @param {string} patientId 
     * @param {string} entityType 
     * @param {string} entityId 
     * @param {object} updates 
     */
    async updateEntityAssociation(patientId, entityType, entityId, updates) {
        try {
            // Since there's no direct endpoint, we fetch, update array, and save patient
            // This is a temporary client-side workaround until backend supports granular patches
            const patientRes = await this.getPatientById(patientId);
            const patient = patientRes.data || patientRes;

            const associations = patient.entityAssociations || [];
            const idx = associations.findIndex(a => a.entityType === entityType && a.entityId === entityId);

            if (idx === -1) throw new Error("Association not found");

            associations[idx] = { ...associations[idx], ...updates };

            await this.updatePatient(patientId, { entityAssociations: associations });
            return associations[idx];
        } catch (error) {
            console.error("Error updating entity association:", error);
            throw error;
        }
    }

    /**
     * Get the history of entity associations for a patient
     * @param {string} patientId
     * @param {string} [entityType] - Optional: Filter by entity type
     */
    async getEntityHistory(patientId, entityType) {
        try {
            const query = entityType ? `?entityType=${entityType}` : '';
            const response = await apiClient.get(`${this.BASE_URL}/${patientId}/associations${query}`);
            return response;
        } catch (error) {
            console.error("Error getting entity history:", error);
            throw error;
        }
    }

    /**
     * Apply claim information to all claims with missing data for a patient
     * @param {string} patientId
     * @param {object} claimInfo - { caseType, insuranceClaimId, dateOfInjury }
     */
    async applyClaimInfoToAll(patientId, claimInfo) {
        try {
            const response = await apiClient.post(`${this.BASE_URL}/${patientId}/claims/apply-all`, claimInfo);
            return response;
        } catch (error) {
            console.error("Error applying claim info to all claims:", error);
            throw error;
        }
    }

    /**
     * @deprecated Use searchPatients() instead
     * Get patient search index (minimal data for all patients)
     */
    async getSearchIndex() {
        console.warn('getSearchIndex() is deprecated. Use searchPatients() for scalable search.');
        const response = await apiClient.get(`${this.BASE_URL}/search-index`);
        return response;
    }

    /**
     * Get patient-level intelligence summary
     * @param {string} id 
     */
    async getPatientIntelligence(id) {
        const response = await apiClient.get(`/patients-intelligence/${id}`);
        return response;
    }

    /**
     * Trigger recomputation of patient-level intelligence
     * @param {string} id 
     */
    async recomputePatientIntelligence(id) {
        const response = await apiClient.post(`/patients-intelligence/${id}/recompute`);
        return response;
    }
}

export const patientService = new PatientService();
